import { MouseEvent } from 'react';
import styled, { css } from 'styled-components/macro';
import { colourNeutral09 } from '../styling/colours';
import { fontSize01 } from '../styling/fonts';
import { shadow2 } from '../styling/shadows';
import { spacing02 } from '../styling/spacing';
import { ButtonProps, SecondaryButton } from './Button';
import { ReactComponent as PlusIcon } from '../images/icons/add.svg';
import { ReactComponent as TickIcon } from '../images/icons/check.svg';
import { ReactComponent as CrossIcon } from '../images/icons/cross.svg';

type Props = {
  isVisible: boolean;
  options: Array<PopoverOption>;
  onAfterSelectMenuItem: () => void;
  closeMenu: () => void;
};

export type PopoverOption = {
  name: string;
  icon: 'Plus' | 'Tick' | 'Cross';
  dataTestId: string;
  onClick: () => void;
};

export const PopoverMenu = ({ isVisible, options, onAfterSelectMenuItem, closeMenu }: Props) => {
  const handleClick = (option: PopoverOption): void => {
    option.onClick();
    closeMenu();
    onAfterSelectMenuItem();
  };

  const handleMouseDownCapture = (event: MouseEvent) => {
    // This prevents selecting the menu items from blurring the button and making onClick not fire
    event.preventDefault();
  };

  return (
    <>
      {isVisible && (
        <PopoverMenuContainer>
          {options.map((option, key) => (
            <PopoverOptionButton
              key={key}
              data-testid={option.dataTestId}
              onClick={() => handleClick(option)}
              onMouseDownCapture={handleMouseDownCapture}
            >
              <PopoverMenuIcon iconType={option.icon} />
              {option.name}
            </PopoverOptionButton>
          ))}
        </PopoverMenuContainer>
      )}
    </>
  );
};

type MenuIconProps = {
  iconType: 'Plus' | 'Tick' | 'Cross';
};
const PopoverMenuIcon = ({ iconType }: MenuIconProps) => {
  switch (iconType) {
    case 'Plus':
      return <PopoverMenuPlusIcon />;
    case 'Tick':
      return <PopoverMenuTickIcon />;
    case 'Cross':
      return <PopoverMenuCrossIcon />;
  }
};

const plusButtonSizeInPixels = 24;
const plusButtonSize = `${plusButtonSizeInPixels}px`;
const popoverRightAlign = '-12px';

const PopoverMenuContainer = styled.div`
  position: fixed;
  top: 34px;
  right: ${popoverRightAlign};
  background: white;
  display: flex;
  flex-direction: column;
  box-shadow: ${shadow2};
`;

const PopoverOptionButton = styled(SecondaryButton)<ButtonProps>`
  height: ${plusButtonSize};
  padding: ${spacing02};
  font-size: ${fontSize01};
  justify-content: start;
  border: none;
  &:hover,
  &:focus {
    background-color: ${colourNeutral09};
  }
`;

const iconStyles = css`
  width: 10px;
  height: 10px;
  margin-right: 2px;
`;

const PopoverMenuPlusIcon = styled(PlusIcon)`
  ${iconStyles}
`;

const PopoverMenuTickIcon = styled(TickIcon)`
  ${iconStyles}
`;

const PopoverMenuCrossIcon = styled(CrossIcon)`
  ${iconStyles}
`;
